// PWA Utility Functions
// Generated by Windsurf Product Developer Intelligence — Task 30 Execution

export class PWAManager {
  constructor() {
    this.deferredPrompt = null;
    this.isInstallable = false;
    this.isInstalled = false;
    this.isOffline = !navigator.onLine;
    this.swRegistration = null;
    
    this.init();
  }

  async init() {
    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      console.log('[PWA] Install prompt available');
      e.preventDefault();
      this.deferredPrompt = e;
      this.isInstallable = true;
      this.onInstallPromptAvailable?.(e);
    });

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      console.log('[PWA] App installed successfully');
      this.isInstalled = true;
      this.isInstallable = false;
      this.deferredPrompt = null;
      this.onAppInstalled?.();
    });

    // Listen for online/offline events
    window.addEventListener('online', () => {
      console.log('[PWA] Connection restored');
      this.isOffline = false;
      this.onConnectionChange?.(false);
    });

    window.addEventListener('offline', () => {
      console.log('[PWA] Connection lost');
      this.isOffline = true;
      this.onConnectionChange?.(true);
    });

    // Register service worker
    await this.registerServiceWorker();
  }

  async registerServiceWorker() {
    if ('serviceWorker' in navigator) {
      try {
        console.log('[PWA] Registering service worker...');
        this.swRegistration = await navigator.serviceWorker.register('/service-worker.js', {
          updateViaCache: 'none'
        });
        console.log('[PWA] Service worker registered:', this.swRegistration.scope);
        
        // Listen for service worker updates
        this.swRegistration.addEventListener('updatefound', () => {
          console.log('[PWA] Service worker update found');
          const newWorker = this.swRegistration.installing;
          
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('[PWA] New content available, please refresh');
              this.onUpdateAvailable?.();
            }
          });
        });

        return true;
      } catch (error) {
        console.error('[PWA] Service worker registration failed:', error);
        return false;
      }
    } else {
      console.warn('[PWA] Service workers not supported');
      return false;
    }
  }

  async promptInstall() {
    if (!this.deferredPrompt) {
      console.warn('[PWA] Install prompt not available');
      return false;
    }

    try {
      console.log('[PWA] Showing install prompt');
      const result = await this.deferredPrompt.prompt();
      console.log('[PWA] Install prompt result:', result);
      
      this.deferredPrompt = null;
      this.isInstallable = false;
      
      return result.outcome === 'accepted';
    } catch (error) {
      console.error('[PWA] Install prompt failed:', error);
      return false;
    }
  }

  async checkUpdate() {
    if (!this.swRegistration) {
      return false;
    }

    try {
      await this.swRegistration.update();
      return true;
    } catch (error) {
      console.error('[PWA] Update check failed:', error);
      return false;
    }
  }

  async skipWaiting() {
    if (!this.swRegistration || !this.swRegistration.waiting) {
      return false;
    }

    try {
      this.swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
      return true;
    } catch (error) {
      console.error('[PWA] Skip waiting failed:', error);
      return false;
    }
  }

  // Get network status
  getNetworkStatus() {
    return {
      isOnline: navigator.onLine,
      isOffline: this.isOffline,
      connection: navigator.connection || navigator.mozConnection || navigator.webkitConnection,
      effectiveType: navigator.connection?.effectiveType || 'unknown'
    };
  }

  // Get PWA installation status
  getInstallStatus() {
    return {
      isInstallable: this.isInstallable,
      isInstalled: this.isInstalled,
      isStandalone: this.isStandaloneMode(),
      displayMode: this.getDisplayMode()
    };
  }

  // Check if running in standalone mode
  isStandaloneMode() {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true ||
      document.referrer.includes('android-app://')
    );
  }

  // Get current display mode
  getDisplayMode() {
    if (this.isStandaloneMode()) {
      return 'standalone';
    }
    if (window.matchMedia('(display-mode: minimal-ui)').matches) {
      return 'minimal-ui';
    }
    if (window.matchMedia('(display-mode: browser)').matches) {
      return 'browser';
    }
    return 'unknown';
  }

  // Get service worker status
  getServiceWorkerStatus() {
    return {
      supported: 'serviceWorker' in navigator,
      registered: !!this.swRegistration,
      scope: this.swRegistration?.scope || null,
      controller: !!navigator.serviceWorker.controller
    };
  }

  // Event callbacks (to be set by components)
  onInstallPromptAvailable = null;
  onAppInstalled = null;
  onConnectionChange = null;
  onUpdateAvailable = null;
}

// Create singleton instance
export const pwaManager = new PWAManager();

// Utility functions
export const isPWASupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

export const canInstallPWA = () => {
  return pwaManager.isInstallable;
};

export const isPWAInstalled = () => {
  return pwaManager.isInstalled || pwaManager.isStandaloneMode();
};

export const getNetworkInfo = () => {
  return pwaManager.getNetworkStatus();
};

export const requestNotificationPermission = async () => {
  if ('Notification' in window) {
    const permission = await Notification.requestPermission();
    console.log('[PWA] Notification permission:', permission);
    return permission;
  }
  return 'denied';
};

export const showLocalNotification = (title, options = {}) => {
  if (Notification.permission === 'granted') {
    return new Notification(title, {
      icon: '/icons/icon-192x192.png',
      badge: '/icons/icon-96x96.png',
      ...options
    });
  }
  return null;
};

// Cache management utilities
export const clearCache = async () => {
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('[PWA] All caches cleared');
      return true;
    } catch (error) {
      console.error('[PWA] Cache clear failed:', error);
      return false;
    }
  }
  return false;
};

export const getCacheSize = async () => {
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    try {
      const estimate = await navigator.storage.estimate();
      return {
        quota: estimate.quota,
        usage: estimate.usage,
        usageDetails: estimate.usageDetails
      };
    } catch (error) {
      console.error('[PWA] Storage estimate failed:', error);
      return null;
    }
  }
  return null;
};

export default pwaManager;
